import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column }    from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Button }    from 'primereact/button';
import { Toast }     from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown }  from 'primereact/dropdown';
import { fetchRailwayDetails, updateRailwayDetails } from './Railwayapi';
import { Tag } from 'primereact/tag';


export default function RailwayTable() {
  // table data + loading + pagination
  const [records, setRecords]           = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading]           = useState(false);
  const [page, setPage]                 = useState(0);
  const [rows, setRows]                 = useState(10);
  const toast                           = useRef(null);

  // filter inputs
  const [nameFilter, setNameFilter]     = useState('');
  const [phoneFilter, setPhoneFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState(null);

  // this object captures the *applied* filters
  const [filters, setFilters] = useState({});

  // dropdown options for status
  const statusOptions = [
    { label: 'Pending',  value: '0' },
    { label: 'Accepted', value: '1' },
    { label: 'Rejected', value: '2' }
  ];

    useEffect(() => {
       
        let userData = JSON.parse(localStorage.getItem("userData"));
        console.log(userData?.role,"edrftghuj");
    }, []);

  // helper to render status text
  const statusBodyTemplate = row => {
    switch (row.isUserVerified) {
      case '0': return 'Pending';
      case '1': return 'Accepted';
      case '2': return 'Rejected';
      default:  return row.isUserVerified;
    }
  };

  // loadData now picks up page, rows and whatever filters are set
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: page + 1,
        pageSize: rows,
        // only send them if non-empty:
        ...(filters.firstName  && { firstName:  filters.firstName  }),
        ...(filters.phone      && { phone:      filters.phone      }),
        ...(filters.isUserVerified && { isUserVerified: filters.isUserVerified })
      };

      const response = await fetchRailwayDetails(params);
      const items    = response.data.data;
      setRecords(items);

      // derive totalRecords either from your payload or from array length
      const total = items.length > 0 && items[0].totalSize
        ? items[0].totalSize
        : items.length;
      setTotalRecords(total);

    } catch (err) {
      toast.current.show({
        severity: 'error',
        summary:  'Load Failed',
        detail:   err.message
      });
    } finally {
      setLoading(false);
    }
  }, [page, rows, filters]);

  // initial load & whenever page/rows/filters change
  useEffect(() => {
    loadData();
  }, [loadData]);

// inside RailwayTable.jsx

// when “Search” is clicked:
const handleSearch = () => {
    // reset to first page
    setPage(0);
  
    fetchRailwayDetails({
      pageNumber:      1,
      pageSize:        rows,
      firstName:       nameFilter.trim()     || undefined,
      mobileNumber:    phoneFilter.trim()    || undefined,
      isUserVerified:  statusFilter          // could be "0","1","2" or null
    })
      .then(resp => {
        const items = resp.data.data;
        setRecords(items);
        setTotalRecords(items[0]?.totalSize ?? items.length);
      })
      .catch(err => {
        toast.current.show({ severity:'error', summary:'Load Failed', detail: err.message });
      });
  };
  
  

  // reset everything
  const handleClear = () => {
    setNameFilter('');
    setPhoneFilter('');
    setStatusFilter(null);
    setPage(0);
    setFilters({});
  };

  const onVerify = async row => {
    try {
      await updateRailwayDetails({ ...row, isUserVerified: '1' });
      toast.current.show({
        severity: 'success',
        summary:  'Verified',
        detail:   `${row.patientCode} set to verified`
      });
      loadData();
    } catch (err) {
      toast.current.show({
        severity: 'error',
        summary:  'Update Failed',
        detail:   err.message
      });
    }
  };

  const onPageChange = e => {
    setPage(e.page);
    setRows(e.rows);
  };

    const handleStatusChange = async (rowData, newStatus) => {
      try {
        const payload = {
          patientCode:     rowData.patientCode,
          isUserVerified:  String(newStatus), // "1" or "2"
          adminCode:       'AMS'
        };
        await updateRailwayDetails(payload);    // calls your PUT /updateDetails
        toast.current.show({
          severity: 'success',
          summary:  newStatus ===  '1' ? 'Accepted' : 'Rejected',
          detail:   `${rowData.patientCode} → ${ newStatus === '1' ? 'Accepted' : 'Rejected' }`
        });
        // reload current page
        loadData();  
      } catch (err) {
        toast.current.show({
          severity: 'error',
          summary:  'Update Failed',
          detail:   err.message
        });
      }
    };

  return (
    <div>
      <Toast ref={toast} />

      {/* ──────────────── external filters ──────────────── */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap',marginTop:'80px' }}>
        <InputText
          placeholder="Search by Name"
          value={nameFilter}
          onChange={e => setNameFilter(e.target.value)}
        />

        <InputText
          placeholder="Search by Phone"
          value={phoneFilter}
          onChange={e => setPhoneFilter(e.target.value)}
        />

        <Dropdown
          placeholder="Select Status"
          value={statusFilter}
          options={statusOptions}
          onChange={e => setStatusFilter(e.value)}
        />

        <Button label="Search" icon="pi pi-search" onClick={handleSearch} />
        <Button label="Clear Filters" className="p-button-secondary" onClick={handleClear} />
      </div>

      {/* ──────────────── your table ──────────────── */}
      <DataTable
        value={records}
        dataKey="patientCode"
        loading={loading}
        paginator={false}    /* we use external Paginator below */
        rows={rows}
        responsiveLayout="scroll"
        lazy                 /* so pagination shows spinner */
      >
        <Column field="patientCode"      header="Patient Code" />
        <Column field="firstName"        header="First Name" />
        <Column field="lastName"         header="Last Name" />
        <Column field="email"            header="Email" />
        <Column field="phone"            header="Phone" />

        <Column
  header="Status"
  body={row => {
    let severity, label;
    switch (row.isUserVerified) {
      case '0':
        severity = 'warning';
        label    = 'Pending';
        break;
      case '1':
        severity = 'success';
        label    = 'Accepted';
        break;
      case '2':
        severity = 'danger';
        label    = 'Rejected';
        break;
      default:
        severity = 'info';
        label    = row.isUserVerified;
    }
    return <Tag value={label} severity={severity} />;
  }}
/>


        <Column field="railwayUserId"    header="Railway ID" />

        <Column
          header="ID Image"
          body={row => (
            <a
              href={`${process.env.REACT_APP_IMG_BASEURL}${row.railwayUserIdName}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color:           '#255b89',
                textDecoration:  'underline',
                fontWeight:      'bold'
              }}
            >
              View
            </a>
          )}
        />

        <Column
          header="Action"
          body={rowData => {
            // if already accepted/rejected, just show text
            if (rowData.isUserVerified === '1') {
              return <span style={{ color: 'green',fontWeight:"700" }}>Accepted</span>;
            }
            if (rowData.isUserVerified === '2') {
              return <span style={{ color: 'red' }}>Rejected</span>;
            }

            // otherwise show both buttons
            return (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  icon="pi pi-check"
                  className="p-button-rounded p-button-success"
                  tooltip="Accept"
                  onClick={() => handleStatusChange(rowData, '1')}
                  size="small"
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-rounded p-button-danger"
                  tooltip="Reject"
                  onClick={() => handleStatusChange(rowData, '2')}
                  size="small"
                />
              </div>
            );
          }}
        />

      </DataTable>

      {/* ──────────────── pagination ──────────────── */}
      <Paginator
        first={page * rows}
        rows={rows}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        rowsPerPageOptions={[5, 10, 20, 50]}
      />
    </div>
  );
}
